/**
 * Hlavička mimo zápasu: logo, navigácia medzi obrazovkami, hlavná výzva
 * (vytvoriť stôl) a kto je prihlásený. Zápas má vlastnú, hustejšiu hlavičku
 * (`GameHeader`), lebo hra potrebuje každý pixel výšky.
 */

import type { Route } from '../App.js';
import { Logo } from '../games/unmatched/ui/Logo.js';
import { hasUnseen } from './changelog.js';
import { PortalLogo } from './PortalLogo.js';

interface Props {
  route: Route;
  nick: string;
  onNavigate: (route: Route) => void;
  onLogout: () => void;
}

const PORTAL_NAV: { label: string; route: Route; match: Route['name'][] }[] = [
  { label: 'Hry', route: { name: 'portal' }, match: ['portal'] },
  { label: 'Patch notes', route: { name: 'changelog' }, match: ['changelog'] },
];

const DUNE_NAV: { label: string; route: Route; match: Route['name'][] }[] = [
  { label: 'Stoly', route: { name: 'dune', path: '' }, match: ['dune'] },
];

const KARAK_NAV: { label: string; route: Route; match: Route['name'][] }[] = [
  { label: 'Stoly', route: { name: 'karak', path: '' }, match: ['karak'] },
];

const MURA_NAV: { label: string; route: Route; match: Route['name'][] }[] = [
  { label: 'Hra', route: { name: 'mura', path: '' }, match: ['mura'] },
];

/** Pravidlá v PDF — v novej karte, hra ostane otvorená. */
const RULES_LINKS: Record<'unmatched' | 'dune' | 'karak' | 'mura', { label: string; href: string }[]> = {
  mura: [],
  unmatched: [{ label: 'Pravidlá', href: '/api/rules/core' }],
  dune: [
    { label: 'Pravidlá', href: '/api/dune/rules/rulebook' },
    { label: 'FAQ', href: '/api/dune/rules/faq' },
  ],
  karak: [
    { label: 'Pravidlá', href: '/api/karak/rules/rulebook' },
    { label: 'Regent', href: '/api/karak/rules/regent' },
  ],
};

const NAV: { label: string; route: Route; match: Route['name'][] }[] = [
  { label: 'Lobby & stoly', route: { name: 'home' }, match: ['home', 'new'] },
  { label: 'Zápasy', route: { name: 'matches' }, match: ['matches'] },
  { label: 'Knižnica kariet', route: { name: 'library', heroId: null }, match: ['library'] },
];

export function AppHeader({ route, nick, onNavigate, onLogout }: Props) {
  // Na portáli je hlavička portálu; v hre logo hry a cesta späť na portál.
  // Patch notes patria portálu — hlavička ostáva portálová.
  const inPortal = route.name === 'portal' || route.name === 'changelog';
  const unseen = route.name !== 'changelog' && hasUnseen();
  const inDune = route.name === 'dune';
  const inKarak = route.name === 'karak';
  const inMura = route.name === 'mura';
  return (
    <header className="appHeader">
      {inPortal ? (
        <button type="button" className="appHeader__brand" onClick={() => onNavigate({ name: 'portal' })}>
          <PortalLogo />
        </button>
      ) : (
        <span className="appHeader__brands">
          <button
            type="button"
            className="appHeader__back"
            onClick={() => onNavigate({ name: 'portal' })}
            title="Späť na portál Hearth & Table"
          >
            <PortalLogo compact />
          </button>
          <span className="appHeader__sep">/</span>
          {inMura ? (
            <button type="button" className="appHeader__brand" onClick={() => onNavigate({ name: 'mura', path: '' })}>
              <span className="logo">
                <span className="logo__text">
                  <span className="logo__name" style={{ fontFamily: "'Cinzel', Georgia, serif", letterSpacing: '0.24em' }}>Mura</span>
                  <span className="logo__tag">demo</span>
                </span>
              </span>
            </button>
          ) : inKarak ? (
            <button type="button" className="appHeader__brand" onClick={() => onNavigate({ name: 'karak', path: '' })}>
              <span className="logo">
                <span className="logo__text">
                  <span className="logo__name" style={{ fontFamily: "'Cinzel', Georgia, serif", letterSpacing: '0.12em' }}>Karak</span>
                </span>
              </span>
            </button>
          ) : inDune ? (
            <button
              type="button"
              className="appHeader__brand"
              onClick={() => onNavigate({ name: 'dune', path: '' })}
            >
              <span className="logo">
                <span className="logo__text">
                  <span className="logo__name">Dune</span>
                  <span className="logo__tag">1.0</span>
                </span>
              </span>
            </button>
          ) : (
            <button type="button" className="appHeader__brand" onClick={() => onNavigate({ name: 'home' })}>
              <Logo />
            </button>
          )}
        </span>
      )}

      <nav className="appHeader__nav">
        {(inPortal ? PORTAL_NAV : inDune ? DUNE_NAV : inKarak ? KARAK_NAV : inMura ? MURA_NAV : NAV).map((item) => (
          <button
            key={item.label}
            type="button"
            className={`navTab ${item.match.includes(route.name) ? 'is-active' : ''}`}
            onClick={() => onNavigate(item.route)}
          >
            {item.label}
            {item.route.name === 'changelog' && unseen ? (
              <span className="navTab__dot" aria-label="nové" />
            ) : null}
          </button>
        ))}
        {!inPortal
          ? RULES_LINKS[inDune ? 'dune' : inKarak ? 'karak' : inMura ? 'mura' : 'unmatched'].map((link) => (
              // Oficiálne pravidlá v PDF — v novej karte, hra ostane otvorená.
              <a key={link.href} className="navTab navTab--external" href={link.href} target="_blank" rel="noopener">
                {link.label}
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                  <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                </svg>
                <span className="visually-hidden"> (PDF, otvorí sa v novej karte)</span>
              </a>
            ))
          : null}
      </nav>

      <div className="appHeader__right">
        {route.name !== 'new' && !inPortal && !inDune && !inKarak && !inMura ? (
          <button
            type="button"
            className="btn btn--accent"
            onClick={() => onNavigate({ name: 'new' })}
          >
            + Vytvoriť stôl
          </button>
        ) : null}
        <span className="userChip">
          <span className="avatar">{initials(nick)}</span>
          <span className="userChip__nick">{nick}</span>
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onLogout}>
          Odhlásiť
        </button>
      </div>
    </header>
  );
}

/** Dve písmená do avatara — z prezývky, iné údaje o hráčovi nemáme. */
export function initials(nick: string): string {
  const parts = nick
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters =
    parts.length > 1 ? parts[0]!.slice(0, 1) + parts[1]!.slice(0, 1) : nick.slice(0, 2);
  return letters.toUpperCase() || '?';
}
